import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import RequirementCard from "../components/tracker/RequirementCard";
import MyStagesStrip from "../components/tracker/MyStagesStrip";
import { AuthContext } from "../auth/AuthContext";
import { useEnabledDepartments } from "../hooks/useEnabledDepartments";
import { TRACKER_COLORS as C, MONO, STATE_TABS } from "../constants/trackerUi";
import {
  useTrackerPortfolio,
  useProjectRequirements,
  useEligibleOwners,
  useRaiseRequirement,
} from "../api/trackerApi";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 9,
  border: `1px solid ${C.line}`,
  fontSize: "0.88rem",
  fontFamily: "inherit",
  color: C.ink,
  background: C.surface,
};

const label = {
  display: "block",
  fontFamily: MONO,
  fontSize: "0.62rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: C.inkFaint,
  marginBottom: 6,
};

/** Raise a requirement and hand its first stage to someone. */
function RaiseModal({ projectId, fallbackProjectId, projects, departments, userDepartment, onClose, onRaised }) {
  /**
   * Department leads. The server derives a requirement's department from the
   * project it lands on, so this drives the project list rather than being
   * sent as a separate field — two sources of truth for one value could
   * disagree, and the server's would silently win.
   */
  const initialDepartment =
    projects.find((p) => p.projectId === (projectId || fallbackProjectId))?.department ||
    userDepartment ||
    "";
  const [chosenDepartment, setChosenDepartment] = useState(initialDepartment);

  // Starts on whatever was selected on the wall, falling back to the
  // department's general project — but it can be changed here, since the
  // right home for a requirement isn't always the project you were looking at.
  const [chosenProjectId, setChosenProjectId] = useState(projectId || fallbackProjectId || "");

  /**
   * The canonical DEPT-<KEY> container for a department, mirroring the server's
   * own departmentProjectId() slug rule.
   *
   * Matched by id rather than by taking the first container claiming the
   * department: a stale container can carry another department's key, and
   * picking it would resolve owners from the wrong department entirely.
   */
  const departmentHomeId = useMemo(() => {
    if (!chosenDepartment) return "";
    const canonical = `DEPT-${String(chosenDepartment)
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "-")
      .replace(/^-|-$/g, "")}`;
    const homes = projects.filter(
      (p) => p.isDepartmentProject && p.department === chosenDepartment
    );
    return (homes.find((p) => p.projectId === canonical) || homes[0])?.projectId || "";
  }, [projects, chosenDepartment]);

  /**
   * Who the requirement can be assigned to.
   *
   * Falls back to the department's own container when no project is chosen:
   * "No specific project" is a normal choice, and the owners query is disabled
   * on an empty id, so without this the list was simply empty — which is also
   * where the requirement would land, so it is the right roster to offer.
   */
  const ownerProjectId = chosenProjectId || departmentHomeId;
  const { data: owners } = useEligibleOwners(ownerProjectId, "requirement");
  const raise = useRaiseRequirement();
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "High",
    ownerId: "",
    targetDate: "",
  });
  const [checklist, setChecklist] = useState([""]);

  /**
   * Where the department catalog comes from.
   *
   * The portfolio carries it, but only on backends new enough to include it.
   * /config/departments is what Settings itself writes, so reading that as a
   * fallback means the HOD works regardless of which backend is deployed
   * rather than failing silently on the older one.
   */
  const { departmentRows } = useEnabledDepartments();
  const catalogRows = useMemo(() => {
    if (departments?.length) return departments;
    return (departmentRows || []).map((d) => ({
      key: d.key,
      label: d.displayName || d.key,
      managerId: d.managerId ?? null,
      // /config/departments carries the id but not the name; the dropdown
      // resolves it from the roster, so a missing name costs nothing.
      managerName: null,
    }));
  }, [departments, departmentRows]);

  /**
   * Departments to offer, merged per key rather than chosen wholesale.
   *
   * An earlier version dropped to a projects-only fallback that omitted
   * managerId entirely, so the HOD field never filled — no error, nothing to
   * explain it. Merging keeps every department's configured head intact even
   * when one source is missing or a department has no project yet.
   */
  const departmentOptions = useMemo(() => {
    const catalog = new Map(
      (catalogRows || []).map((d) => [d.key, d])
    );
    const keys = [
      ...new Set([
        ...(catalogRows || []).map((d) => d.key),
        ...projects.map((p) => p.department),
      ]),
    ].filter(Boolean);

    return keys.map((k) => {
      const row = catalog.get(k);
      return {
        key: k,
        label:
          row?.label ||
          projects.find((p) => p.department === k)?.departmentLabel ||
          k,
        // Undefined and null both mean "no configured HOD"; normalise so the
        // consumer only has one absent case to handle.
        managerId: row?.managerId ?? null,
        managerName: row?.managerName ?? null,
      };
    });
  }, [catalogRows, projects]);

  const configuredHod = departmentOptions.find((d) => d.key === chosenDepartment) || null;

  /**
   * Pre-select the department's configured HOD as the assignee, but only while
   * the field is untouched — switching project or department clears it, which
   * is what lets the next default fill in.
   *
   * Guarded on the owner list containing them: the server rejects an owner who
   * is not a member of a real project, so pre-filling someone absent from it
   * would fail only on submit.
   */
  const configuredHodId = configuredHod?.managerId ?? null;
  const hodIsEligible = (owners || []).some(
    (m) => String(m.id) === String(configuredHodId || "")
  );
  useEffect(() => {
    if (!configuredHodId || !hodIsEligible) return;
    setForm((f) => (f.ownerId ? f : { ...f, ownerId: String(configuredHodId) }));
  }, [configuredHodId, hodIsEligible]);

  /** Only this department's real projects — the department's own container is
      what "no project" already means, so listing it twice would be confusing. */
  const departmentProjects = useMemo(
    () => projects.filter((p) => p.department === chosenDepartment && !p.isDepartmentProject),
    [projects, chosenDepartment]
  );

  // Hold the page still while the dialog is open, so scrolling inside it
  // doesn't drag the wall along behind.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /** Switching project invalidates the owner — they may not be on the new one. */
  const changeProject = (next) => {
    setChosenProjectId(next);
    setForm((f) => ({ ...f, ownerId: "" }));
  };

  /**
   * Switching department clears the project too: keeping the old one would
   * file the requirement under the department it belongs to, quietly ignoring
   * the department just chosen.
   */
  const changeDepartment = (next) => {
    setChosenDepartment(next);
    setChosenProjectId("");
    // Clear the HOD too: it belongs to the department being left, and clearing
    // it is what lets the new department's configured head fill in.
    setForm((f) => ({ ...f, ownerId: "" }));
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Give it a title");
    if (!form.ownerId) return toast.error("Assign it to someone");
    try {
      /* Project is optional. With none chosen it lands in the chosen
         department's own container — the same one the owner list above is
         drawn from, so the person offered is always someone the server will
         accept. The server would otherwise default to the raiser's own
         department, which is not necessarily the one on the form. */
      const departmentHome = departmentHomeId;
      const created = await raise.mutateAsync({
        projectId: chosenProjectId || departmentHome || undefined,
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        ownerId: Number(form.ownerId),
        // The raiser's target for the requirement. The owner's own commitment
        // is the stage ETA, still set when they accept.
        targetDate: form.targetDate || null,
        // The assignee is the head it answers to — one person, one field.
        hodId: form.ownerId ? Number(form.ownerId) : null,
        checklist: checklist.map((c) => c.trim()).filter(Boolean).map((l) => ({ label: l })),
      });
      toast.success("Requirement raised");
      // Hand back what was created so the page can show it: a new requirement
      // rests in Backlog until someone accepts, which is not the tab you are
      // usually on when you raise one.
      onRaised?.({
        id: created?.id,
        projectId: chosenProjectId || created?.projectId || departmentHome || null,
      });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not raise requirement");
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          borderRadius: 16,
          width: "100%",
          maxWidth: 760,
          maxHeight: "calc(100vh - 2rem)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* header stays put while the form scrolls */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: `1px solid ${C.line}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>Raise a requirement</h3>
            <p style={{ margin: "3px 0 0", fontSize: "0.8rem", color: C.inkFaint }}>
              Who it's for, what's needed, and when.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ border: "none", background: "none", fontSize: 22, cursor: "pointer", color: C.inkFaint, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {/* Who it is for, before what it is: the department decides which
              projects and which owners are even offered below. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 12 }}>
            <label>
              <span style={label}>Department</span>
              <select
                value={chosenDepartment}
                onChange={(e) => changeDepartment(e.target.value)}
                style={inputStyle}
              >
                {departmentOptions.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>

            {/* One field, not two: whoever this is assigned to is the head it
                answers to. Pre-fills with the department's configured HOD but
                stays changeable — a stand-in covers a leave, and a
                cross-cutting ask answers to whoever sponsored it.

                Options come from the project's eligible owners rather than
                department leadership: the server requires the owner to be a
                member of a real project, so a leadership-only list could name
                someone it would then reject. */}
            <label>
              <span style={label}>Assign to</span>
              <select
                value={form.ownerId}
                onChange={(e) => set("ownerId", e.target.value)}
                style={inputStyle}
              >
                <option value="">
                  {(owners || []).length ? "Choose someone…" : "No one available"}
                </option>
                {(owners || []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.preferred ? " ★" : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label>
            <span style={label}>Project (optional)</span>
            <select value={chosenProjectId} onChange={(e) => changeProject(e.target.value)} style={inputStyle}>
              {/* Optional on purpose: with none chosen it is filed under the
                  department itself, so raising never waits on a project. */}
              <option value="">No specific project</option>
              {departmentProjects.map((p) => (
                <option key={p.projectId} value={p.projectId}>
                  {p.projectName}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span style={label}>Title</span>
            {/* Generic on purpose: this form raises requirements for every
                department, and the title becomes the epic name on the board,
                so the hint asks for something short rather than showing one
                team's example. */}
            <input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="A short title for what you need…"
              style={inputStyle}
            />
          </label>

          <label>
            <span style={label}>Description</span>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Business context, expected outcome, how we'll know it worked…"
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </label>

          {/* The points that spell out the ask belong with it, not after the
              scheduling fields. */}
          <div>
            <span style={label}>Checklist (optional)</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {checklist.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 7, alignItems: "center" }}>
                  <input
                    value={item}
                    onChange={(e) => setChecklist((c) => c.map((x, j) => (j === i ? e.target.value : x)))}
                    placeholder={`Point ${i + 1}`}
                    style={inputStyle}
                  />
                  {checklist.length > 1 ? (
                    <button
                      onClick={() => setChecklist((c) => c.filter((_, j) => j !== i))}
                      aria-label={`Remove point ${i + 1}`}
                      style={{
                        border: `1px solid ${C.line}`,
                        background: C.surface,
                        borderRadius: 8,
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        cursor: "pointer",
                        color: C.inkFaint,
                        fontSize: "1rem",
                      }}
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            <button
              onClick={() => setChecklist((c) => [...c, ""])}
              style={{
                marginTop: 8,
                border: `1px solid ${C.line}`,
                background: C.surface,
                borderRadius: 8,
                padding: "7px 13px",
                fontSize: "0.78rem",
                fontWeight: 700,
                cursor: "pointer",
                color: C.inkSoft,
              }}
            >
              ＋ Add point
            </button>
          </div>

          {/* Scheduling last. This ETA is the raiser's target date for the
              requirement — the owner's own commitment is the stage ETA they
              set when they accept, which this does not touch. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: 12 }}>
            <label>
              <span style={label}>Priority</span>
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)} style={inputStyle}>
                {["Critical", "High", "Medium", "Low"].map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
            <label>
              <span style={label}>ETA (optional)</span>
              <input
                type="date"
                value={form.targetDate}
                onChange={(e) => set("targetDate", e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>

        </div>

        {/* actions stay visible however long the form gets */}
        <div
          style={{
            display: "flex",
            gap: 9,
            padding: "14px 24px",
            borderTop: `1px solid ${C.line}`,
            background: C.sunken,
            flexShrink: 0,
          }}
        >
          <button
            onClick={submit}
            disabled={raise.isPending}
            style={{ border: "none", background: C.accent, color: "#fff", borderRadius: 9, padding: "10px 19px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
          >
            {raise.isPending ? "Raising…" : "Raise requirement"}
          </button>
          <button
            onClick={onClose}
            style={{ border: `1px solid ${C.line}`, background: C.surface, color: C.inkSoft, borderRadius: 9, padding: "10px 19px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, selected, onSelect, stateKey }) {
  const n = project.counts?.[stateKey] ?? 0;
  return (
    <button
      onClick={() => onSelect(project.projectId)}
      style={{
        flex: "0 0 auto",
        minWidth: 200,
        maxWidth: 200,
        textAlign: "left",
        background: selected ? C.accentSoft : C.surface,
        border: `1.5px solid ${selected ? C.accent : C.line}`,
        borderRadius: 12,
        padding: "12px 14px",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          fontFamily: MONO,
          fontSize: "0.58rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          padding: "2px 6px",
          borderRadius: 100,
          background: selected ? "rgba(52,87,213,0.14)" : C.sunken,
          color: selected ? C.accentInk : C.inkFaint,
        }}
      >
        {project.isDepartmentProject ? `${project.departmentLabel} · general` : project.departmentLabel}
      </span>
      <div style={{ fontSize: "0.85rem", fontWeight: 700, margin: "8px 0 5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {project.projectName}
      </div>
      <div style={{ fontSize: "0.71rem", color: C.inkFaint }}>
        {n} {n === 1 ? "requirement" : "requirements"}
        {project.counts?.total ? ` · ${project.counts.total} total` : ""}
      </div>
    </button>
  );
}

export default function Tracker() {
  const { user } = useContext(AuthContext);

  // Arriving from My Tasks or a notification: ?project=&requirement=&state=
  // seeds the same selections the strip's onJump makes, so a linked-to
  // requirement is on screen and open rather than merely somewhere on the page.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedProjectId = searchParams.get("project") || null;
  const linkedRequirementId = searchParams.get("requirement") || null;
  const linkedState = searchParams.get("state") || null;

  const [stateKey, setStateKey] = useState(
    linkedState && STATE_TABS.some((t) => t.key === linkedState) ? linkedState : "active"
  );
  const [projectId, setProjectId] = useState(linkedProjectId);
  const [raising, setRaising] = useState(false);
  // Held in state so clearing the URL params below doesn't collapse the card
  // we just opened.
  const [focusRequirementId, setFocusRequirementId] = useState(linkedRequirementId);
  /**
   * Whether the focused card should also open. Captured once, for the same
   * reason focusRequirementId is: the effect below strips the URL params after
   * honouring them, and reading them live would flip this to false mid-session.
   */
  const [expandFocused] = useState(Boolean(linkedRequirementId));

  const { data: portfolio, isLoading, error } = useTrackerPortfolio();
  const projects = useMemo(() => portfolio?.projects || [], [portfolio]);

  /**
   * The carousel shows the projects that actually have work in the selected
   * state — a wall of "0 requirements" cards tells you nothing. The tab counts
   * still come from every project, so the totals stay honest.
   */
  const visibleProjects = useMemo(
    () => projects.filter((p) => (p.counts?.[stateKey] ?? 0) > 0),
    [projects, stateKey]
  );

  // Follow the visible set: switching state should land on something real
  // rather than leaving the list below empty.
  useEffect(() => {
    // Hold the deep-linked project until the portfolio has loaded, otherwise
    // this would bump the selection away before the target project appears.
    if (linkedProjectId && !projects.length) return;
    if (!visibleProjects.length) {
      setProjectId(null);
      return;
    }
    if (!visibleProjects.some((p) => p.projectId === projectId)) {
      setProjectId(visibleProjects[0].projectId);
    }
  }, [visibleProjects, projectId, linkedProjectId, projects.length]);

  // The link has been honoured — drop the params so a later refresh or tab
  // change isn't dragged back to where the user originally landed.
  useEffect(() => {
    if (!linkedProjectId && !linkedRequirementId && !linkedState) return;
    if (!projects.length) return;
    const next = new URLSearchParams(searchParams);
    ["project", "requirement", "state"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [projects.length, linkedProjectId, linkedRequirementId, linkedState, searchParams, setSearchParams]);

  const { data: detail, isLoading: loadingReqs } = useProjectRequirements(projectId, stateKey);
  const requirements = detail?.requirements || [];
  const selectedProject = projects.find((p) => p.projectId === projectId);

  const canRaise = (user?.permissions || []).includes("tracker.requirement.raise");

  if (isLoading) return <div style={{ padding: "2rem", color: C.inkFaint }}>Loading Tracker…</div>;
  if (error) return <div style={{ padding: "2rem", color: C.danger }}>Could not load Tracker.</div>;

  return (
    <div style={{ paddingBottom: "3rem" }}>
      <style>{`
        @keyframes trackerPulse{0%,100%{box-shadow:0 0 0 0 rgba(52,87,213,.5)}50%{box-shadow:0 0 0 4px rgba(52,87,213,0)}}
        /* The live stage on the rail — a slow breath, not a flash. */
        @keyframes trackerStagePulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.28);opacity:.75}}
        @media (prefers-reduced-motion: reduce){
          [style*="trackerStagePulse"],[style*="trackerPulse"]{animation:none !important}
        }
      `}</style>

      {/* Layout already renders the department switcher above every page. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: "0 0 3px", fontSize: "1.28rem", fontWeight: 700, color: C.ink }}>Tracker</h2>
          <div style={{ color: C.inkSoft, fontSize: "0.84rem" }}>
            Every project, one screen — pick a state, pick a project, open a requirement.
          </div>
        </div>
      </div>

      {/* what's sitting with me right now, wherever it lives */}
      <MyStagesStrip
        onJump={(s) => {
          if (s.projectId) setProjectId(s.projectId);
          if (s.status === "blocked") setStateKey("on_hold");
          else setStateKey("active");
        }}
      />

      {/* state tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", background: C.surface, border: `1px solid ${C.line}`, borderRadius: 12, padding: 5, width: "fit-content", marginBottom: 16 }}>
        {STATE_TABS.map((tab) => {
          const total = projects.reduce((sum, p) => sum + (p.counts?.[tab.key] ?? 0), 0);
          const sel = stateKey === tab.key;
          const warn = tab.tone === "warn";
          return (
            <button
              key={tab.key}
              onClick={() => setStateKey(tab.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontSize: "0.8rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
                background: sel ? (warn ? C.warn : C.accent) : "transparent",
                color: sel ? "#fff" : warn ? C.warn : C.inkSoft,
              }}
            >
              {tab.label}
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  padding: "1px 7px",
                  borderRadius: 100,
                  minWidth: 20,
                  textAlign: "center",
                  background: sel ? "rgba(255,255,255,0.22)" : warn ? C.warnSoft : C.sunken,
                  color: sel ? "#fff" : warn ? C.warn : C.inkFaint,
                }}
              >
                {total}
              </span>
            </button>
          );
        })}
      </div>

      {/* project carousel */}
      {visibleProjects.length ? (
        <>
          <div style={{ fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.07em", textTransform: "uppercase", color: C.inkFaint, marginBottom: 9 }}>
            Projects with {STATE_TABS.find((t) => t.key === stateKey)?.label.toLowerCase()} work
          </div>
          <div style={{ display: "flex", gap: 11, overflowX: "auto", paddingBottom: 6, marginBottom: 24 }}>
            {visibleProjects.map((p) => (
              <ProjectCard key={p.projectId} project={p} selected={p.projectId === projectId} onSelect={setProjectId} stateKey={stateKey} />
            ))}
          </div>
        </>
      ) : (
        <div
          style={{
            padding: "2.2rem 1.5rem",
            textAlign: "center",
            background: C.surface,
            border: `1px dashed ${C.line}`,
            borderRadius: 13,
            marginBottom: 24,
          }}
        >
          <div style={{ fontSize: "0.92rem", fontWeight: 700, marginBottom: 5 }}>
            {projects.length
              ? `Nothing ${STATE_TABS.find((t) => t.key === stateKey)?.label.toLowerCase()} right now`
              : "No projects you can see yet"}
          </div>
          <div style={{ color: C.inkFaint, fontSize: "0.84rem", marginBottom: canRaise && projects.length ? 14 : 0 }}>
            {projects.length
              ? "Try another state above, or raise something new."
              : "Ask an admin to add you to a project."}
          </div>
          {canRaise && projects.length ? (
            <button
              onClick={() => setRaising(true)}
              style={{ border: "none", background: C.accent, color: "#fff", borderRadius: 9, padding: "9px 17px", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}
            >
              ＋ Raise requirement
            </button>
          ) : null}
        </div>
      )}

      {/* requirement list */}
      {selectedProject ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 11 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>{selectedProject.projectName}</h3>
              <span style={{ fontSize: "0.76rem", color: C.inkFaint }}>
                {selectedProject.departmentLabel}
                {detail ? ` · ${requirements.length} ${STATE_TABS.find((t) => t.key === stateKey)?.label.toLowerCase()}` : ""}
                {detail && !detail.usesSprints ? " · no sprints" : ""}
              </span>
            </div>
            {canRaise ? (
              <button
                onClick={() => setRaising(true)}
                style={{ border: "none", background: C.accent, color: "#fff", borderRadius: 9, padding: "8px 15px", fontWeight: 700, fontSize: "0.79rem", cursor: "pointer" }}
              >
                ＋ Raise requirement
              </button>
            ) : null}
          </div>

          {loadingReqs ? (
            <div style={{ color: C.inkFaint, fontSize: "0.85rem", padding: "1rem 0" }}>Loading requirements…</div>
          ) : requirements.length === 0 ? (
            <div
              style={{
                padding: "2.2rem 1rem",
                textAlign: "center",
                color: C.inkFaint,
                fontSize: "0.87rem",
                background: C.surface,
                border: `1px dashed ${C.line}`,
                borderRadius: 13,
              }}
            >
              Nothing {STATE_TABS.find((t) => t.key === stateKey)?.label.toLowerCase()} on this project.
              {canRaise ? " Raise a requirement to start the flow." : ""}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {requirements.map((r) => (
                <RequirementCard
                  key={r.id}
                  requirement={r}
                  autoFocus={String(r.id) === String(focusRequirementId)}
                  /* A deep-link opens the card it points at; a freshly raised
                     one is only highlighted, so the list stays a list. */
                  openOnFocus={
                    expandFocused && String(r.id) === String(focusRequirementId)
                  }
                  usesSprints={Boolean(detail?.usesSprints)}
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {/* With no project selected the server files it under the department's
          general project, so raising still works from an empty state. */}
      {raising ? (
        <RaiseModal
          projectId={projectId}
          fallbackProjectId={
            projects.find((p) => p.isDepartmentProject && p.department === user?.department)?.projectId
          }
          /* Every project, not just those matching the active tab — where a
             requirement belongs has nothing to do with which state you're viewing. */
          projects={projects}
          departments={portfolio?.departments}
          userDepartment={user?.department}
          onClose={() => setRaising(false)}
          onRaised={({ id, projectId: raisedProjectId }) => {
            // A raised requirement starts in Backlog — land the user there,
            // on the right project, with the new card highlighted, rather
            // than on an Active tab that cannot show it yet.
            setStateKey("backlog");
            if (raisedProjectId) setProjectId(raisedProjectId);
            if (id) setFocusRequirementId(String(id));
          }}
        />
      ) : null}
    </div>
  );
}
